import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { decodePaymentEmailToken } from '../utils/paymentEmailToken';
import { isAllOrganisationsSelected } from '../utils/organisation';

/**
 * Custom hook to resolve payment parameters from URL.
 * Handles both regular payment URLs and email-sourced URLs with encoded tokens.
 * 
 * @returns {{ resolvedPaymentId: string, resolvedOrgId: string|number, isFromEmail: boolean }}
 */
export const useResolvedPaymentParams = () => {
    const { paymentId } = useParams();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();

    const isFromEmail = searchParams.get('src') === 'email';
    const orgIdParam = searchParams.get('orgId');

    const { resolvedPaymentId, resolvedOrgId } = useMemo(() => {
        if (isFromEmail) {
            const decoded = decodePaymentEmailToken(paymentId);
            if (decoded) {
                return {
                    resolvedPaymentId: decoded.paymentUuid,
                    resolvedOrgId: decoded.organisationId
                };
            }
        }
        // When arriving from the multi-organisation payments list, an explicit
        // orgId identifies which organisation the payment belongs to. Otherwise
        // fall back to the active organisation, but never the "All Organisations"
        // aggregate sentinel, which downstream payment/user/authorisation calls
        // cannot resolve.
        const fallbackOrgId = isAllOrganisationsSelected(user)
            ? user.organisations?.[0]?.id
            : user.organisationId;
        return {
            resolvedPaymentId: paymentId,
            resolvedOrgId: orgIdParam || fallbackOrgId
        };
    }, [isFromEmail, paymentId, orgIdParam, user]);

    return { resolvedPaymentId, resolvedOrgId, isFromEmail };
};

